import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { TaskCardActivity } from './TaskCardActivity.jsx';
import { TaskDetailsText } from './TaskDetailsText.jsx';
import { clientLabel } from '../utils/clientLabel.js';
import { boardColumns as normalizeBoardColumns, DEFAULT_TASK_COLUMNS, stageInk, stageWash } from '../utils/taskProgress.js';
import { SearchableSelect } from './SearchableSelect.jsx';
import {
  PRIORITIES,
  QUICK_FILTERS,
  TASK_KINDS,
  WAITING_ON,
  dueIso,
  isOverdue,
  listSections,
  priorityLabel,
  taskKind,
  todayIso,
  visibleQueueTasks,
  waitingDetail,
} from '../utils/taskQueue.js';
import { TaskStageEditor } from './TaskStageEditor.jsx';

export const STAFF_TASK_COLUMNS = DEFAULT_TASK_COLUMNS.map((column) => ({ id: column.key, label: column.name }));
export const CLIENT_TASK_COLUMNS = DEFAULT_TASK_COLUMNS.filter((column) => column.visible_to_client).map((column) => ({
  id: column.key,
  label: column.name,
}));

function formatDue(value) {
  const iso = dueIso(value);
  if (!iso) return '';
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const sameYear = year === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });
}

function formatStamp(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function initials(name) {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

function groupTasks(tasks, columns) {
  const grouped = Object.fromEntries(columns.map((column) => [column.id, []]));
  const rest = [];
  const cancelled = [];
  const sorted = [...tasks].sort(
    (a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || Number(a.id) - Number(b.id),
  );
  for (const task of sorted) {
    if (task.status === 'cancelled') cancelled.push(task);
    else if (grouped[task.status]) grouped[task.status].push(task);
    else rest.push(task);
  }
  return { grouped, rest, cancelled };
}

function applyMove(tasks, columns, taskId, toStatus, toIndex) {
  const { grouped, rest, cancelled } = groupTasks(tasks, columns);
  let fromStatus = null;
  let fromIndex = -1;
  for (const column of columns) {
    const index = grouped[column.id].findIndex((task) => task.id === taskId);
    if (index >= 0) {
      fromStatus = column.id;
      fromIndex = index;
      break;
    }
  }
  let fromRest = false;
  if (fromStatus == null) {
    fromIndex = rest.findIndex((task) => task.id === taskId);
    if (fromIndex < 0 || !grouped[toStatus]) return null;
    fromRest = true;
  }
  if (!grouped[toStatus]) return null;

  let index = toIndex;
  if (!fromRest && fromStatus === toStatus && fromIndex < index) index -= 1;
  if (index < 0) index = 0;
  const [item] = fromRest ? rest.splice(fromIndex, 1) : grouped[fromStatus].splice(fromIndex, 1);
  if (index > grouped[toStatus].length) index = grouped[toStatus].length;
  if (fromStatus === toStatus && fromIndex === index) return null;
  grouped[toStatus].splice(index, 0, { ...item, status: toStatus });

  const next = [...rest, ...cancelled];
  for (const column of columns) {
    grouped[column.id].forEach((task, order) => {
      next.push({ ...task, status: column.id, sort_order: order });
    });
  }
  return next;
}

function toDraft(task) {
  return {
    title: task.title || '',
    description: task.description || '',
    due_date: dueIso(task.due_date),
    assigned_to: task.assigned_to ? String(task.assigned_to) : '',
    kind: taskKind(task),
    priority: task.priority || 'medium',
    status: task.status || 'open',
    waiting_on: task.waiting_on || '',
    waiting_user_id: task.waiting_user_id ? String(task.waiting_user_id) : '',
    waiting_reason: task.waiting_reason || '',
    start_date: dueIso(task.start_date),
    related_task_id: task.related_task_id ? String(task.related_task_id) : '',
    progress_weight: Number(task.progress_weight ?? 1),
    checklist: (task.checklist_items || []).map((item) => ({
      id: item.id,
      title: item.title || '',
      is_done: Boolean(item.is_done),
    })),
  };
}

function WaitingMemberSelect({ value, people, onChange, compact = false }) {
  const select = (
    <SearchableSelect aria-label="Team member" value={value} onChange={onChange} required>
      <option value="">{compact ? 'Team member…' : 'Choose a team member…'}</option>
      {people.map((person) => (
        <option key={person.id} value={person.id}>
          {person.name}
          {person.role ? ` (${person.role})` : ''}
        </option>
      ))}
    </SearchableSelect>
  );
  if (compact) return select;
  return (
    <label className="field">
      <span>Team member</span>
      {select}
    </label>
  );
}

function StageMark({ column }) {
  if (column.marks_complete) {
    return (
      <span className="task-column-mark" aria-hidden>
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" fill="currentColor" />
          <path d="M7.5 12.4 10.6 15.5 16.6 9" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (column.key === 'waiting') {
    return (
      <span className="task-column-mark" aria-hidden>
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="8.25" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M12 7.75V12l2.75 1.75" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (column.key === 'in_progress') {
    return (
      <span className="task-column-mark" aria-hidden>
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="8.25" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M12 5.1a6.9 6.9 0 0 1 0 13.8Z" fill="currentColor" />
        </svg>
      </span>
    );
  }
  return (
    <span className="task-column-mark" aria-hidden>
      <svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="8.25" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    </span>
  );
}

function TaskFace({ task, showProject, today, columns }) {
  const due = dueIso(task.due_date);
  const overdue = isOverdue(task, columns, today);
  const waiting = task.status === 'waiting';
  const request = taskKind(task) === 'client_request';
  const checklist = task.checklist_items || [];
  const checklistDone = checklist.filter((item) => item.is_done).length;
  const client = clientLabel(task.client) || clientLabel(task.project?.client);

  return (
    <>
      <span className="task-card-title">{task.title}</span>
      {client || (showProject && task.project?.name) ? (
        <span className="task-card-project">
          {[client, showProject ? task.project?.name : ''].filter(Boolean).join(' · ')}
        </span>
      ) : null}
      <span className="task-card-meta">
        <span className={`task-chip is-${task.priority || 'medium'}`}>{priorityLabel(task.priority)}</span>
        {request ? <span className="task-chip is-request">Client request</span> : null}
        {due ? <span className={`task-chip${overdue ? ' is-overdue' : ''}`}>{overdue ? 'Overdue · ' : ''}{formatDue(task.due_date)}</span> : null}
        {waiting ? <span className="task-chip is-waiting">Waiting{waitingDetail(task) ? ` · ${waitingDetail(task)}` : ''}</span> : null}
        {checklist.length ? <span className="task-chip">{checklistDone}/{checklist.length}</span> : null}
        {task.assignee?.name ? <span className="task-card-assignee">{task.assignee.name}</span> : <span className="task-card-assignee is-empty">Unassigned</span>}
      </span>
    </>
  );
}

/**
 * Team work queue. Board view is the drag-and-drop kanban. List view groups
 * the same tasks by when they are due.
 */
export function TaskBoard({
  tasks,
  onChange,
  mode = 'admin',
  columns,
  projectId = null,
  projects = [],
  assignees = [],
  clientAssignees = [],
  canDelete = () => false,
  onError,
  onColumnsChange,
  stageScope = 'project',
  showProject = false,
  initialTaskId = null,
  onFiltersChange,
}) {
  const boardColumns = normalizeBoardColumns(columns, { client: mode === 'client' }).filter((column) => column.key !== 'cancelled');
  const canEdit = mode === 'admin';
  const reorderUrl = mode === 'client' ? '/api/my/tasks/reorder' : '/api/admin/tasks/reorder';
  const today = todayIso();
  const [view, setView] = useState('board');
  const [quick, setQuick] = useState('');
  const [clientId, setClientId] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [priority, setPriority] = useState('');
  const filters = {
    quick,
    clientId: projectId ? '' : clientId,
    projectId: projectId ? '' : projectFilter,
    assigneeId: mode === 'admin' ? assigneeId : '',
    priority: mode === 'admin' ? priority : '',
  };
  const visibleTasks = useMemo(
    () => visibleQueueTasks(tasks, filters, boardColumns),
    [tasks, quick, clientId, projectFilter, assigneeId, priority, projectId, mode, boardColumns],
  );

  useEffect(() => {
    onFiltersChange?.({
      quick,
      client_id: filters.clientId,
      project_id: filters.projectId,
      assigned_to: filters.assigneeId,
      priority: filters.priority,
    });
  }, [quick, clientId, projectFilter, assigneeId, priority]);
  const sections = useMemo(() => listSections(visibleTasks, boardColumns, quick), [visibleTasks, boardColumns, quick]);
  const { grouped, rest } = useMemo(() => groupTasks(visibleTasks, boardColumns), [visibleTasks, boardColumns]);
  const visibleColumns = rest.length
    ? [...boardColumns, { id: 'other', label: 'Other', locked: true }]
    : boardColumns;

  const [dragId, setDragId] = useState(null);
  const [dropOver, setDropOver] = useState('');
  const [composer, setComposer] = useState(null);
  const [composerTitle, setComposerTitle] = useState('');
  const [composerAssignee, setComposerAssignee] = useState('');
  const [composerProjectId, setComposerProjectId] = useState(projectId ? String(projectId) : '');
  const [composerWaitingOn, setComposerWaitingOn] = useState('');
  const [composerWaitingUser, setComposerWaitingUser] = useState('');
  const [creating, setCreating] = useState(false);
  const [openTask, setOpenTask] = useState(null);
  const [draft, setDraft] = useState(null);
  const [clientPeople, setClientPeople] = useState(clientAssignees);
  const [saving, setSaving] = useState(false);
  const [menuMode, setMenuMode] = useState('');
  const [moveProjectId, setMoveProjectId] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [waitingMove, setWaitingMove] = useState(null);
  const [loadedProjects, setLoadedProjects] = useState([]);
  const activityRef = useRef(null);
  const openedRef = useRef(false);

  const projectChoices = projects.length ? projects : loadedProjects;

  const clients = useMemo(() => {
    const map = new Map();
    for (const project of projectChoices) {
      if (project.client?.id) map.set(project.client.id, project.client);
    }
    for (const task of tasks) {
      if (task.project?.client?.id) map.set(task.project.client.id, task.project.client);
    }
    return [...map.values()];
  }, [projectChoices, tasks]);

  const assigneeOptions = useMemo(() => {
    const map = new Map();
    for (const person of assignees) map.set(String(person.id), person.name);
    for (const task of tasks) {
      if (task.assignee?.id) map.set(String(task.assignee.id), task.assignee.name);
    }
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [assignees, tasks]);

  const staffOptions = useMemo(() => {
    const map = new Map();
    for (const person of assignees) {
      map.set(String(person.id), { id: String(person.id), name: person.name, role: person.role || '' });
    }
    for (const task of tasks) {
      if (task.waiting_user?.id && !map.has(String(task.waiting_user.id))) {
        map.set(String(task.waiting_user.id), { id: String(task.waiting_user.id), name: task.waiting_user.name, role: '' });
      }
    }
    return [...map.values()];
  }, [assignees, tasks]);

  useEffect(() => {
    setClientPeople(clientAssignees);
  }, [clientAssignees]);

  useEffect(() => {
    if (menuMode !== 'move' || projects.length || mode !== 'admin') return undefined;
    let cancelled = false;
    apiJson('/api/admin/projects')
      .then((rows) => {
        if (!cancelled) setLoadedProjects(Array.isArray(rows) ? rows : rows?.data || []);
      })
      .catch(() => {
        if (!cancelled) setLoadedProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [menuMode, projects.length, mode]);

  useEffect(() => {
    if (!openTask || mode !== 'admin' || draft?.kind !== 'client_request') return undefined;
    const nextClientId = openTask.project?.client?.id;
    if (!nextClientId) return undefined;
    let cancelled = false;
    apiJson(`/api/admin/tasks/client-assignees?client_id=${nextClientId}`)
      .then((rows) => {
        if (!cancelled) setClientPeople(rows);
      })
      .catch(() => {
        if (!cancelled) setClientPeople([]);
      });
    return () => {
      cancelled = true;
    };
  }, [openTask, draft?.kind, mode]);

  useEffect(() => {
    if (!openTask) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape' || saving || deleting) return;
      if (menuMode) {
        setMenuMode('');
        return;
      }
      if (waitingMove) {
        setWaitingMove(null);
        return;
      }
      setOpenTask(null);
      setDraft(null);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [openTask, saving, deleting, menuMode, waitingMove]);

  const report = (error) => {
    onError?.(error?.message || 'Something went wrong');
  };

  const openCard = (task) => {
    setOpenTask(task);
    setDraft(toDraft(task));
    setMenuMode('');
    setMoveProjectId('');
  };

  useEffect(() => {
    if (openedRef.current || !initialTaskId) return;
    const match = tasks.find((task) => String(task.id) === String(initialTaskId));
    if (!match) return;
    openedRef.current = true;
    openCard(match);
  }, [initialTaskId, tasks]);

  const closeCard = () => {
    if (saving || deleting) return;
    setOpenTask(null);
    setDraft(null);
    setMenuMode('');
  };

  const replaceTask = (updated) => {
    onChange(tasks.map((task) => (task.id === updated.id ? { ...task, ...updated } : task)));
    setOpenTask(updated);
    setDraft(toDraft(updated));
  };

  const persistOrder = async (next) => {
    const items = next
      .filter((task) => boardColumns.some((column) => column.id === task.status))
      .map((task) => ({
        id: task.id,
        status: task.status,
        sort_order: Number(task.sort_order) || 0,
        ...(task.status === 'waiting'
          ? {
              waiting_on: task.waiting_on || null,
              waiting_user_id: task.waiting_on === 'team_member' ? (task.waiting_user_id || null) : null,
              related_task_id: task.related_task_id || null,
            }
          : {}),
      }));
    if (!items.length) return;
    await apiJson(reorderUrl, { method: 'PUT', body: { items } });
  };

  const commitMove = async (taskId, status, index, waitingPatch = null) => {
    const next = applyMove(tasks, boardColumns, taskId, status, index);
    if (!next) return;
    const patched = next.map((task) => {
      if (task.id !== taskId) return task;
      if (status === 'waiting') return { ...task, ...waitingPatch, status };
      return { ...task, status, waiting_on: null, waiting_user_id: null, waiting_user: null, waiting_since: null, related_task_id: null };
    });
    const previous = tasks;
    onChange(patched);
    if (openTask?.id === taskId) {
      const moved = patched.find((task) => task.id === taskId);
      if (moved) setOpenTask(moved);
    }
    try {
      await persistOrder(patched);
    } catch (error) {
      onChange(previous);
      report(error);
    }
  };

  const onDrop = async (event, status, index) => {
    event.preventDefault();
    event.stopPropagation();
    const taskId = Number(event.dataTransfer.getData('text/plain'));
    setDragId(null);
    setDropOver('');
    if (!taskId || !boardColumns.some((column) => column.id === status)) return;
    const task = tasks.find((item) => item.id === taskId);
    if (!task || (task.status === status && index === undefined)) return;
    if (status === 'waiting' && task.status !== 'waiting' && mode === 'admin') {
      setWaitingMove({
        taskId,
        status,
        index,
        waiting_on: task.waiting_on || 'client',
        waiting_user_id: task.waiting_user_id ? String(task.waiting_user_id) : '',
        related_task_id: task.related_task_id ? String(task.related_task_id) : '',
        projectId: task.project_id,
      });
      return;
    }
    const waitingPatch = status === 'waiting'
      ? { waiting_on: task.waiting_on || 'team_member', related_task_id: task.related_task_id || null }
      : null;
    await commitMove(taskId, status, index, waitingPatch);
  };

  const confirmWaitingMove = async () => {
    if (!waitingMove?.waiting_on) return;
    if (waitingMove.waiting_on === 'team_member' && !waitingMove.waiting_user_id) {
      report({ message: 'Choose the team member this task is waiting on.' });
      return;
    }
    const person = staffOptions.find((item) => item.id === String(waitingMove.waiting_user_id));
    const patch = {
      waiting_on: waitingMove.waiting_on,
      waiting_user_id: waitingMove.waiting_on === 'team_member' ? Number(waitingMove.waiting_user_id) : null,
      waiting_user: waitingMove.waiting_on === 'team_member' && person
        ? { id: Number(person.id), name: person.name }
        : null,
      related_task_id: waitingMove.related_task_id ? Number(waitingMove.related_task_id) : null,
      waiting_since: new Date().toISOString(),
    };
    const move = waitingMove;
    setWaitingMove(null);
    await commitMove(move.taskId, move.status, move.index, patch);
  };

  const startComposer = (status) => {
    setComposer(status);
    setComposerTitle('');
    setComposerWaitingOn(status === 'waiting' ? 'client' : '');
    setComposerWaitingUser('');
    setComposerAssignee('');
    setComposerProjectId(projectId ? String(projectId) : projects[0] ? String(projects[0].id) : '');
  };

  const createCard = async (event) => {
    event.preventDefault();
    const title = composerTitle.trim();
    const nextProjectId = projectId || composerProjectId;
    if (!title || !composer) return;
    if (!composerAssignee) {
      report({ message: 'Assign this task to someone.' });
      return;
    }
    if (composer === 'waiting' && !composerWaitingOn) return;
    if (composer === 'waiting' && composerWaitingOn === 'team_member' && !composerWaitingUser) {
      report({ message: 'Choose the team member this task is waiting on.' });
      return;
    }
    onError?.('');
    setCreating(true);
    try {
      const created = await apiJson('/api/admin/tasks', {
        method: 'POST',
        body: {
          project_id: nextProjectId ? Number(nextProjectId) : null,
          assigned_to: Number(composerAssignee),
          title,
          status: composer,
          ...(composer === 'waiting'
            ? {
                waiting_on: composerWaitingOn,
                waiting_user_id: composerWaitingOn === 'team_member' ? Number(composerWaitingUser) : null,
              }
            : {}),
        },
      });
      onChange([...tasks, created]);
      setComposer(null);
      setComposerTitle('');
      openCard(created);
    } catch (error) {
      report(error);
    } finally {
      setCreating(false);
    }
  };

  const saveCard = async (event) => {
    event?.preventDefault();
    if (!openTask || !draft || !canEdit) return;
    const title = draft.title.trim();
    if (!title) return;
    if (draft.kind === 'team' && !draft.assigned_to) {
      report({ message: 'Assign this task to someone.' });
      return;
    }
    if (draft.status === 'waiting' && !draft.waiting_on) {
      report({ message: 'Choose who this task is waiting on.' });
      return;
    }
    if (draft.status === 'waiting' && draft.waiting_on === 'team_member' && !draft.waiting_user_id) {
      report({ message: 'Choose the team member this task is waiting on.' });
      return;
    }
    setSaving(true);
    try {
      const updated = await apiJson(`/api/admin/tasks/${openTask.id}`, {
        method: 'PUT',
        body: {
          title,
          description: draft.description.trim() || null,
          due_date: draft.due_date || null,
          start_date: draft.start_date || null,
          assigned_to: draft.assigned_to ? Number(draft.assigned_to) : null,
          kind: draft.kind,
          priority: draft.priority,
          status: draft.status,
          waiting_on: draft.status === 'waiting' ? draft.waiting_on : null,
          waiting_user_id: draft.status === 'waiting' && draft.waiting_on === 'team_member' && draft.waiting_user_id
            ? Number(draft.waiting_user_id)
            : null,
          waiting_reason: draft.status === 'waiting' ? (draft.waiting_reason.trim() || null) : null,
          related_task_id: draft.status === 'waiting' && draft.related_task_id ? Number(draft.related_task_id) : null,
          progress_weight: Math.max(0, Math.min(100, Number(draft.progress_weight) || 0)),
          checklist: draft.checklist.map((item) => ({
            id: item.id || undefined,
            title: item.title,
            is_done: Boolean(item.is_done),
          })),
        },
      });
      replaceTask(updated);
    } catch (error) {
      report(error);
    } finally {
      setSaving(false);
    }
  };

  const deleteCard = async () => {
    if (!openTask) return;
    setDeleting(true);
    try {
      await apiJson(`/api/admin/tasks/${openTask.id}`, { method: 'DELETE' });
      onChange(tasks.filter((task) => task.id !== openTask.id));
      setOpenTask(null);
      setDraft(null);
      setMenuMode('');
    } catch (error) {
      report(error);
    } finally {
      setDeleting(false);
    }
  };

  const duplicateCard = async () => {
    if (!openTask) return;
    setSaving(true);
    try {
      const copy = await apiJson(`/api/admin/tasks/${openTask.id}/duplicate`, { method: 'POST' });
      onChange([...tasks, copy]);
      openCard(copy);
    } catch (error) {
      report(error);
    } finally {
      setSaving(false);
    }
  };

  const cancelCard = async () => {
    if (!openTask) return;
    setSaving(true);
    try {
      const updated = await apiJson(`/api/admin/tasks/${openTask.id}`, {
        method: 'PUT',
        body: { status: 'cancelled' },
      });
      onChange(tasks.map((task) => (task.id === updated.id ? { ...task, ...updated } : task)));
      setOpenTask(null);
      setDraft(null);
      setMenuMode('');
      setQuick('cancelled');
      setView('list');
    } catch (error) {
      report(error);
    } finally {
      setSaving(false);
    }
  };

  const moveCard = async () => {
    if (!openTask || !moveProjectId) return;
    setSaving(true);
    try {
      const updated = await apiJson(`/api/admin/tasks/${openTask.id}`, {
        method: 'PUT',
        body: { project_id: Number(moveProjectId) },
      });
      replaceTask(updated);
      setMenuMode('');
    } catch (error) {
      report(error);
    } finally {
      setSaving(false);
    }
  };

  const toggleClientCheck = async (item) => {
    if (!openTask) return;
    const checklist = (openTask.checklist_items || []).map((row) => ({
      id: row.id,
      is_done: row.id === item.id ? !row.is_done : Boolean(row.is_done),
    }));
    try {
      const updated = await apiJson(`/api/my/tasks/${openTask.id}`, { method: 'PUT', body: { checklist } });
      replaceTask({ ...openTask, ...updated });
    } catch (error) {
      report(error);
    }
  };

  const people = draft?.kind === 'client_request' ? clientPeople : assignees;
  const requestChoices = tasks.filter((task) => (
    task.project_id === openTask?.project_id
    && task.id !== openTask?.id
    && taskKind(task) === 'client_request'
  ));
  const waitingChoices = tasks.filter((task) => (
    waitingMove
    && task.project_id === waitingMove.projectId
    && task.id !== waitingMove.taskId
    && taskKind(task) === 'client_request'
  ));

  const columnsToRender = visibleColumns.map((column) => ({
    ...column,
    cards: column.locked ? rest : grouped[column.id] || [],
  }));

  const renderCard = (task, column, index, draggable) => (
    <button
      key={task.id}
      type="button"
      className={`task-card${dragId === task.id ? ' is-dragging' : ''}`}
      draggable={draggable}
      onDragStart={(event) => {
        event.dataTransfer.setData('text/plain', String(task.id));
        event.dataTransfer.effectAllowed = 'move';
        setDragId(task.id);
      }}
      onDragEnd={() => {
        setDragId(null);
        setDropOver('');
      }}
      onDragOver={(event) => {
        if (!draggable || column?.locked) return;
        event.preventDefault();
        event.stopPropagation();
        setDropOver(column.id);
      }}
      onDrop={(event) => {
        if (!draggable || column?.locked) return;
        void onDrop(event, column.id, index);
      }}
      onClick={() => openCard(task)}
    >
      <TaskFace task={task} showProject={showProject} today={today} columns={boardColumns} />
    </button>
  );

  return (
    <div>
      {canEdit && columns?.length && (stageScope === 'agency' || projectId) ? (
        <TaskStageEditor
          projectId={projectId}
          columns={columns}
          scope={stageScope}
          saveUrl={stageScope === 'agency' ? '/api/admin/task-columns' : undefined}
          onSaved={onColumnsChange}
        />
      ) : null}

      <div className="task-toolbar" data-tour="tasks-filters">
        <div className="task-view-toggle" role="group" aria-label="Task view">
          <button type="button" className={view === 'list' ? 'is-selected' : ''} onClick={() => setView('list')}>List</button>
          <button type="button" className={view === 'board' ? 'is-selected' : ''} onClick={() => setView('board')}>Board</button>
        </div>
        <div className="task-quick-filters">
          {QUICK_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={quick === filter.id ? 'is-selected' : ''}
              aria-pressed={quick === filter.id}
              onClick={() => {
                const next = quick === filter.id ? '' : filter.id;
                setQuick(next);
                if (next === 'cancelled') setView('list');
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>
        {mode === 'admin' ? (
          <div className="task-filter-selects">
            {projectId ? null : (
              <SearchableSelect aria-label="Client" value={clientId} onChange={(event) => setClientId(event.target.value)}>
                <option value="">All clients</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>{clientLabel(client)}</option>
                ))}
              </SearchableSelect>
            )}
            {projectId ? null : (
              <SearchableSelect aria-label="Project" value={projectFilter} onChange={(event) => setProjectFilter(event.target.value)}>
                <option value="">All projects</option>
                <option value="none">No project</option>
                {projectChoices.map((project) => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </SearchableSelect>
            )}
            <SearchableSelect aria-label="Assignee" value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>
              <option value="">All assignees</option>
              <option value="none">Unassigned</option>
              {assigneeOptions.map((person) => (
                <option key={person.id} value={person.id}>{person.name}</option>
              ))}
            </SearchableSelect>
            <SearchableSelect aria-label="Priority" value={priority} onChange={(event) => setPriority(event.target.value)}>
              <option value="">All priorities</option>
              {PRIORITIES.map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </SearchableSelect>
          </div>
        ) : null}
      </div>

      {view === 'list' ? (
        <div className="task-list" data-tour="tasks-list">
          {sections.length ? sections.map((section) => (
            <section key={section.id}>
              <h3>{section.label} <span>{section.tasks.length}</span></h3>
              <div className="task-list-cards">
                {section.tasks.map((task) => renderCard(task, null, 0, false))}
              </div>
            </section>
          )) : <p className="muted">Nothing in this view.</p>}
        </div>
      ) : quick === 'cancelled' ? (
        <p className="muted" data-tour="tasks-list">Cancelled tasks stay off the board. Switch to List to review them.</p>
      ) : (
        <div className="task-board" data-tour="tasks-list">
          {columnsToRender.map((column) => (
            <section
              key={column.id}
              className={`task-column${dropOver === column.id ? ' is-drop' : ''}`}
              style={column.locked ? undefined : {
                '--stage-color': stageInk(column.color),
                '--stage-wash': stageWash(column.color, 0.84),
                '--stage-wash-strong': stageWash(column.color, 0.74),
              }}
              onDragOver={(event) => {
                if (column.locked) return;
                event.preventDefault();
                setDropOver(column.id);
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setDropOver('');
              }}
              onDrop={(event) => {
                if (column.locked) return;
                void onDrop(event, column.id, column.cards.length);
              }}
            >
              <header className="task-column-head">
                <div className="task-column-title">
                  {column.locked ? null : <StageMark column={column} />}
                  <div>
                    <h3>{column.label}</h3>
                    {column.locked ? null : (
                      <p className="task-column-progress">
                        {column.counts_toward_progress ? `${column.progress_percent}% complete` : 'Not counted'}
                      </p>
                    )}
                  </div>
                </div>
                <span className="task-column-count">{column.cards.length}</span>
              </header>
              <div className="task-column-cards">
                {column.cards.map((task, index) => renderCard(task, column, index, !column.locked))}
              </div>
              {canEdit && !column.locked ? (
                composer === column.id ? (
                  <form className="task-composer" onSubmit={createCard}>
                    <textarea
                      autoFocus
                      rows={3}
                      value={composerTitle}
                      placeholder="Task name"
                      onChange={(event) => setComposerTitle(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          event.currentTarget.form?.requestSubmit();
                        }
                        if (event.key === 'Escape') setComposer(null);
                      }}
                    />
                    {column.id === 'waiting' ? (
                      <>
                        <SearchableSelect
                          aria-label="Waiting on"
                          value={composerWaitingOn}
                          onChange={(event) => {
                            setComposerWaitingOn(event.target.value);
                            if (event.target.value !== 'team_member') setComposerWaitingUser('');
                          }}
                          required
                        >
                          <option value="">Waiting on…</option>
                          {WAITING_ON.map((item) => (
                            <option key={item.id} value={item.id}>{item.label}</option>
                          ))}
                        </SearchableSelect>
                        {composerWaitingOn === 'team_member' ? (
                          <WaitingMemberSelect compact value={composerWaitingUser} people={staffOptions} onChange={(event) => setComposerWaitingUser(event.target.value)} />
                        ) : null}
                      </>
                    ) : null}
                    {!projectId ? (
                      <SearchableSelect value={composerProjectId} onChange={(event) => setComposerProjectId(event.target.value)} aria-label="Project">
                        <option value="">No project</option>
                        {projectChoices.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                            {clientLabel(project.client) ? ` (${clientLabel(project.client)})` : ''}
                          </option>
                        ))}
                      </SearchableSelect>
                    ) : null}
                    <SearchableSelect aria-label="Assign to" value={composerAssignee} onChange={(event) => setComposerAssignee(event.target.value)} required>
                      <option value="">Assign to…</option>
                      {assigneeOptions.map((person) => (
                        <option key={person.id} value={person.id}>{person.name}</option>
                      ))}
                    </SearchableSelect>
                    <div className="task-composer-actions">
                      <button type="submit" className="btn-primary" disabled={creating || !composerTitle.trim()}>
                        {creating ? 'Adding…' : 'Add task'}
                      </button>
                      <button type="button" className="task-composer-cancel" onClick={() => setComposer(null)} aria-label="Cancel">
                        ×
                      </button>
                    </div>
                  </form>
                ) : (
                  <button type="button" className="task-add" onClick={() => startComposer(column.id)}>
                    + Add task
                  </button>
                )
              ) : null}
            </section>
          ))}
        </div>
      )}

      {waitingMove ? (
        <div className="modal-backdrop" onClick={() => setWaitingMove(null)} role="presentation">
          <form
            className="task-dialog task-waiting-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Waiting details"
            onClick={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault();
              void confirmWaitingMove();
            }}
          >
            <h3>Waiting</h3>
            <label className="field">
              <span>Waiting on</span>
              <SearchableSelect
                value={waitingMove.waiting_on}
                onChange={(event) => setWaitingMove({
                  ...waitingMove,
                  waiting_on: event.target.value,
                  waiting_user_id: event.target.value === 'team_member' ? waitingMove.waiting_user_id : '',
                })}
                required
              >
                {WAITING_ON.map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </SearchableSelect>
            </label>
            {waitingMove.waiting_on === 'team_member' ? (
              <WaitingMemberSelect
                value={waitingMove.waiting_user_id}
                people={staffOptions}
                onChange={(event) => setWaitingMove({ ...waitingMove, waiting_user_id: event.target.value })}
              />
            ) : null}
            <label className="field">
              <span>Related client request</span>
              <SearchableSelect value={waitingMove.related_task_id} onChange={(event) => setWaitingMove({ ...waitingMove, related_task_id: event.target.value })}>
                <option value="">None</option>
                {waitingChoices.map((task) => (
                  <option key={task.id} value={task.id}>{task.title}</option>
                ))}
              </SearchableSelect>
            </label>
            <div className="task-composer-actions">
              <button type="submit" className="btn-primary">Save</button>
              <button type="button" className="btn-secondary" onClick={() => setWaitingMove(null)}>Cancel</button>
            </div>
          </form>
        </div>
      ) : null}

      {openTask && draft ? (
        <div className="modal-backdrop" onClick={closeCard} role="presentation">
          <div
            className="task-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={draft.title || 'Task'}
            onClick={(event) => event.stopPropagation()}
            onPaste={(event) => activityRef.current?.uploadClipboard(event)}
          >
            <button type="button" className="modal-close task-dialog-close" onClick={closeCard} aria-label="Close">
              ×
            </button>
            <div className="task-dialog-layout">
              <div className="task-dialog-main">
                {canEdit ? (
                  <input
                    className="task-dialog-title"
                    aria-label="Task title"
                    value={draft.title}
                    onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                    required
                  />
                ) : (
                  <h3 className="task-dialog-title">{openTask.title}</h3>
                )}
                <p className="muted small task-dialog-kicker">
                  {taskKind(openTask) === 'client_request' ? 'Client request' : 'Team task'}
                  {' · '}
                  {boardColumns.find((column) => column.id === openTask.status)?.label || openTask.status.replaceAll('_', ' ')}
                  {openTask.project?.name ? (
                    <>
                      {' · '}
                      {mode === 'admin' ? (
                        <Link to={`/admin/projects/${openTask.project_id}`}>{openTask.project.name}</Link>
                      ) : (
                        <Link to={`/my/projects/${openTask.project_id}`}>{openTask.project.name}</Link>
                      )}
                    </>
                  ) : null}
                </p>
                <h4>Details</h4>
                {canEdit ? (
                  <textarea
                    rows={8}
                    maxLength={10000}
                    value={draft.description}
                    placeholder="Add a more detailed description…"
                    onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                  />
                ) : (
                  <TaskDetailsText text={openTask.description} />
                )}
                {!canEdit && !openTask.description ? <p className="muted small">No details yet.</p> : null}

                <h4>Checklist</h4>
                {canEdit ? (
                  <ul className="task-checklist">
                    {draft.checklist.map((item, index) => (
                      <li key={item.id || `new-${index}`}>
                        <input
                          type="checkbox"
                          checked={item.is_done}
                          aria-label={`Done: ${item.title || 'step'}`}
                          onChange={(event) => setDraft({
                            ...draft,
                            checklist: draft.checklist.map((row, rowIndex) => (
                              rowIndex === index ? { ...row, is_done: event.target.checked } : row
                            )),
                          })}
                        />
                        <input
                          value={item.title}
                          placeholder="Step"
                          aria-label="Checklist step"
                          onChange={(event) => setDraft({
                            ...draft,
                            checklist: draft.checklist.map((row, rowIndex) => (
                              rowIndex === index ? { ...row, title: event.target.value } : row
                            )),
                          })}
                        />
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => setDraft({
                            ...draft,
                            checklist: draft.checklist.filter((_, rowIndex) => rowIndex !== index),
                          })}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="task-checklist">
                    {(openTask.checklist_items || []).length ? (openTask.checklist_items || []).map((item) => (
                      <li key={item.id}>
                        <input
                          type="checkbox"
                          checked={Boolean(item.is_done)}
                          onChange={() => void toggleClientCheck(item)}
                        />
                        <span className={item.is_done ? 'is-done' : ''}>{item.title}</span>
                      </li>
                    )) : <li className="muted small">No steps yet.</li>}
                  </ul>
                )}
                {canEdit ? (
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => setDraft({ ...draft, checklist: [...draft.checklist, { title: '', is_done: false }] })}
                  >
                    Add step
                  </button>
                ) : null}

                <TaskCardActivity
                  ref={activityRef}
                  mode={mode}
                  taskId={openTask.id}
                  onError={report}
                  onCounts={(counts) => {
                    onChange(
                      tasks.map((task) => (
                        task.id === openTask.id
                          ? { ...task, comments_count: counts.comments, attachments_count: counts.attachments }
                          : task
                      )),
                    );
                  }}
                />
              </div>
              <aside className="task-dialog-side">
                {canEdit ? (
                  <>
                    <div className="task-more">
                      <button type="button" className="btn-secondary" aria-expanded={Boolean(menuMode)} onClick={() => setMenuMode(menuMode ? '' : 'open')}>
                        ••• More
                      </button>
                      {menuMode ? (
                        <div className="task-more-menu" role="menu">
                          {menuMode === 'delete' ? (
                            <div className="task-delete-confirm">
                              <p>Delete this task?</p>
                              <button type="button" className="btn-danger" disabled={deleting} onClick={() => void deleteCard()} data-tour="tasks-delete">
                                {deleting ? 'Deleting…' : 'Delete'}
                              </button>
                              <button type="button" className="btn-secondary" disabled={deleting} onClick={() => setMenuMode('open')}>
                                Keep
                              </button>
                            </div>
                          ) : null}
                          {menuMode === 'cancel' ? (
                            <div className="task-delete-confirm">
                              <p>Cancel this task? It leaves the board and stays in the Cancelled filter.</p>
                              <button type="button" className="btn-secondary" disabled={saving} onClick={() => void cancelCard()}>
                                {saving ? 'Cancelling…' : 'Cancel task'}
                              </button>
                              <button type="button" className="btn-secondary" disabled={saving} onClick={() => setMenuMode('open')}>
                                Keep
                              </button>
                            </div>
                          ) : null}
                          {menuMode === 'move' ? (
                            <div className="task-delete-confirm">
                              <label className="field">
                                <span>Project</span>
                                <SearchableSelect value={moveProjectId} onChange={(event) => setMoveProjectId(event.target.value)}>
                                  <option value="">Choose a project…</option>
                                  {projectChoices.filter((project) => project.id !== openTask.project_id).map((project) => (
                                    <option key={project.id} value={project.id}>{project.name}</option>
                                  ))}
                                </SearchableSelect>
                              </label>
                              <button type="button" className="btn-primary" disabled={saving || !moveProjectId} onClick={() => void moveCard()}>
                                {saving ? 'Moving…' : 'Move'}
                              </button>
                            </div>
                          ) : null}
                          {menuMode === 'open' ? (
                            <>
                              <button type="button" onClick={() => void duplicateCard()}>Duplicate</button>
                              <button type="button" onClick={() => setMenuMode('cancel')}>Cancel</button>
                              <button type="button" onClick={() => setMenuMode('move')}>Move to another project</button>
                              {canDelete(openTask) ? (
                                <button type="button" className="is-danger" onClick={() => setMenuMode('delete')} data-tour="tasks-delete">Delete</button>
                              ) : null}
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <label className="field">
                      <span>Type</span>
                      <SearchableSelect
                        value={draft.kind}
                        onChange={(event) => setDraft({ ...draft, kind: event.target.value, assigned_to: '' })}
                      >
                        {TASK_KINDS.map((item) => (
                          <option key={item.id} value={item.id}>{item.label}</option>
                        ))}
                      </SearchableSelect>
                    </label>
                    <label className="field">
                      <span>Assign to</span>
                      <SearchableSelect value={draft.assigned_to} onChange={(event) => setDraft({ ...draft, assigned_to: event.target.value })}>
                        <option value="">Unassigned</option>
                        {people.map((person) => (
                          <option key={person.id} value={person.id}>
                            {person.name}
                            {person.role ? ` (${person.role})` : ''}
                          </option>
                        ))}
                      </SearchableSelect>
                    </label>
                    <label className="field">
                      <span>Priority</span>
                      <SearchableSelect value={draft.priority} onChange={(event) => setDraft({ ...draft, priority: event.target.value })}>
                        {PRIORITIES.map((item) => (
                          <option key={item.id} value={item.id}>{item.label}</option>
                        ))}
                      </SearchableSelect>
                    </label>
                    <label className="field">
                      <span>Stage</span>
                      <SearchableSelect value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}>
                        {boardColumns.map((column) => (
                          <option key={column.id} value={column.id}>{column.label}</option>
                        ))}
                      </SearchableSelect>
                    </label>
                    {draft.status === 'waiting' ? (
                      <>
                        <label className="field">
                          <span>Waiting on</span>
                          <SearchableSelect
                            value={draft.waiting_on}
                            onChange={(event) => setDraft({
                              ...draft,
                              waiting_on: event.target.value,
                              waiting_user_id: event.target.value === 'team_member' ? draft.waiting_user_id : '',
                            })}
                            required
                          >
                            <option value="">Choose…</option>
                            {WAITING_ON.map((item) => (
                              <option key={item.id} value={item.id}>{item.label}</option>
                            ))}
                          </SearchableSelect>
                        </label>
                        {draft.waiting_on === 'team_member' ? (
                          <WaitingMemberSelect
                            value={draft.waiting_user_id}
                            people={staffOptions}
                            onChange={(event) => setDraft({ ...draft, waiting_user_id: event.target.value })}
                          />
                        ) : null}
                        <label className="field">
                          <span>Waiting reason</span>
                          <input value={draft.waiting_reason} onChange={(event) => setDraft({ ...draft, waiting_reason: event.target.value })} />
                        </label>
                        {openTask.waiting_since ? (
                          <p className="muted small">Waiting since {formatStamp(openTask.waiting_since)}</p>
                        ) : null}
                        <label className="field">
                          <span>Related client request</span>
                          <SearchableSelect value={draft.related_task_id} onChange={(event) => setDraft({ ...draft, related_task_id: event.target.value })}>
                            <option value="">None</option>
                            {requestChoices.map((task) => (
                              <option key={task.id} value={task.id}>{task.title}</option>
                            ))}
                          </SearchableSelect>
                        </label>
                      </>
                    ) : null}
                    <label className="field">
                      <span>Due date</span>
                      <input type="date" value={draft.due_date} onChange={(event) => setDraft({ ...draft, due_date: event.target.value })} />
                      {!draft.due_date ? <span className="muted small">A due date helps this show up in My Work. You can still save without one.</span> : null}
                    </label>
                    <label className="field">
                      <span>Start date</span>
                      <input type="date" value={draft.start_date} onChange={(event) => setDraft({ ...draft, start_date: event.target.value })} />
                    </label>
                    <label className="field">
                      <span>Progress impact</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={draft.progress_weight}
                        onChange={(event) => setDraft({ ...draft, progress_weight: event.target.value })}
                      />
                      <span className="muted small">0 ignores this task. Heavier tasks move the project bar more.</span>
                    </label>
                    <button type="button" className="btn-primary" disabled={saving || !draft.title.trim()} onClick={() => void saveCard()}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="muted small">{priorityLabel(openTask.priority)} priority</p>
                    {openTask.due_date ? <p className="muted small">Due {formatDue(openTask.due_date)}</p> : null}
                    {openTask.status === 'waiting' ? (
                      <p className="muted small">
                        Waiting{waitingDetail(openTask) ? ` on ${waitingDetail(openTask)}` : ''}
                        {openTask.waiting_since ? ` since ${formatStamp(openTask.waiting_since)}` : ''}
                      </p>
                    ) : null}
                    <p className="muted small">Drag the card to another column to update its stage.</p>
                  </>
                )}
              </aside>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
